import { jsPDF } from "jspdf";

const dateKey = (value) => String(value || "").slice(0, 10);
const monthLabel = (month) => new Date(`${month}-01T12:00:00`).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
const statusTone = (status) => status?.startsWith("Concluída") ? "done" : status === "Aguardando liberação da área" ? "waiting" : ["Atrasada", "Vence hoje"].includes(status) ? "late" : status === "Em andamento" ? "progress" : "planned";
const palette = {
  done: { fill: [225, 246, 236], line: [27, 151, 103], text: [21, 106, 75] },
  late: { fill: [255, 234, 236], line: [216, 65, 80], text: [164, 40, 54] },
  progress: { fill: [240, 235, 255], line: [113, 82, 202], text: [87, 64, 158] },
  waiting: { fill: [243, 239, 226], line: [150, 129, 85], text: [98, 82, 52] },
  planned: { fill: [232, 241, 255], line: [50, 111, 230], text: [37, 85, 166] },
};
const put = (pdf, value, x, y, width, size = 8, style = "normal") => {
  pdf.setFont("helvetica", style);
  pdf.setFontSize(size);
  pdf.text(pdf.splitTextToSize(String(value || ""), width)[0] || "", x, y);
};
const loadLogo = (src) => new Promise((resolve) => {
  const image = new Image();
  image.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.getContext("2d").drawImage(image, 0, 0);
    resolve(canvas.toDataURL("image/png"));
  };
  image.onerror = () => resolve(null);
  image.src = src;
});
const addLogo = (pdf, image, x, y, maxWidth, maxHeight) => {
  if (!image) return;
  const dimensions = pdf.getImageProperties(image);
  const scale = Math.min(maxWidth / dimensions.width, maxHeight / dimensions.height);
  const width = dimensions.width * scale, height = dimensions.height * scale;
  pdf.addImage(image, "PNG", x + (maxWidth - width) / 2, y + (maxHeight - height) / 2, width, height, undefined, "SLOW");
};
const monthKeys = (start, end) => {
  const keys = [];
  const cursor = new Date(`${start}-01T12:00:00`);
  const last = new Date(`${end}-01T12:00:00`);
  while (cursor <= last && keys.length < 120) {
    keys.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return keys;
};
const metrics = (items) => ({
  done: items.filter((item) => statusTone(item.displayStatus) === "done").length,
  late: items.filter((item) => statusTone(item.displayStatus) === "late").length,
  progress: items.filter((item) => statusTone(item.displayStatus) === "progress").length,
  waiting: items.filter((item) => statusTone(item.displayStatus) === "waiting").length,
  planned: items.filter((item) => statusTone(item.displayStatus) === "planned").length,
});

export async function exportPreventiveAgendaPdf({ items, clientName, unitName, periodLabel, logoImages, download = true }) {
  if (!items.length) return;
  const sorted = [...items].sort((a, b) => dateKey(a.plannedDate).localeCompare(dateKey(b.plannedDate)));
  const firstMonth = dateKey(sorted[0].plannedDate).slice(0, 7);
  const lastMonth = dateKey(sorted[sorted.length - 1].plannedDate).slice(0, 7);
  const months = periodLabel ? [firstMonth] : monthKeys(firstMonth, lastMonth);
  const period = periodLabel || (firstMonth === lastMonth ? monthLabel(firstMonth) : `${monthLabel(firstMonth)} a ${monthLabel(lastMonth)}`);
  const [planLogo, secLogo] = logoImages || await Promise.all([loadLogo("/aboutplan-logo-white-transparent.png"), loadLogo("/aboutsec-logo-white.png")]);
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  const width = 297, height = 210;
  pdf.setFillColor(21, 25, 26); pdf.rect(0, 0, width, height, "F");
  addLogo(pdf, planLogo, 18, 16, 90, 23);
  if (!planLogo) { pdf.setTextColor(255, 255, 255); put(pdf, "ABOUTPLAN", 18, 30, 88, 15, "bold"); }
  addLogo(pdf, secLogo, 254, 13, 25, 28);
  if (!secLogo) { pdf.setTextColor(255, 255, 255); put(pdf, "ABOUTSEC", 248, 29, 35, 9, "bold"); }
  const coverEyebrow = "PROGRAMAÇÃO DE MANUTENÇÃO PREVENTIVA";
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.8);
  const eyebrowWidth = Math.max(87, pdf.getTextWidth(coverEyebrow) + 14);
  pdf.setFillColor(117, 103, 71); pdf.roundedRect(18, 50, eyebrowWidth, 7, 3.5, 3.5, "F");
  pdf.setTextColor(255, 255, 255); pdf.text(coverEyebrow, 18 + eyebrowWidth / 2, 55, { align: "center" });
  pdf.setDrawColor(187, 161, 108); pdf.setLineWidth(0.6); pdf.line(18, 81, 279, 81);
  pdf.setTextColor(255, 255, 255); put(pdf, "AGENDA DE PREVENTIVAS", 18, 106, 250, 28, "bold");
  pdf.setTextColor(187, 191, 186); put(pdf, period.toUpperCase(), 18, 121, 250, 15, "bold");
  pdf.setFillColor(32, 37, 38); pdf.roundedRect(18, 137, 190, 38, 5, 5, "F");
  pdf.setTextColor(187, 161, 108); put(pdf, "CLIENTE / UNIDADE", 26, 149, 170, 9, "bold");
  pdf.setTextColor(255, 255, 255); put(pdf, clientName, 26, 160, 165, 16, "bold");
  put(pdf, unitName, 26, 169, 165, 10);
  pdf.setTextColor(255, 255, 255); put(pdf, `${items.length} visitas · ${months.length} mês(es)`, 219, 150, 62, 11, "bold");
  const summary = metrics(items);
  pdf.setTextColor(187, 191, 186); put(pdf, `${summary.planned} programadas · ${summary.waiting} aguardando área · ${summary.done} concluídas`, 219, 161, 62, 8);
  pdf.setTextColor(187, 161, 108); put(pdf, "ABOUTPLAN  •  GESTÃO INTELIGENTE", 18, 196, 200, 8, "bold");

  months.forEach((month, index) => {
    pdf.addPage();
    pdf.setFillColor(248, 250, 254); pdf.rect(0, 0, width, height, "F");
    pdf.setFillColor(31, 37, 38); pdf.rect(0, 0, width, 45, "F");
    pdf.setFillColor(43, 50, 51); pdf.rect(0, 0, width, 22, "F");
    pdf.setFillColor(187, 161, 108); pdf.rect(0, 0, 2, 45, "F");
    addLogo(pdf, planLogo, 12, 2, 64, 18);
    if (!planLogo) { pdf.setTextColor(255, 255, 255); put(pdf, "ABOUTPLAN", 12, 13, 62, 11, "bold"); }
    addLogo(pdf, secLogo, 266, 1, 19, 20);
    if (!secLogo) { pdf.setTextColor(255, 255, 255); put(pdf, "ABOUTSEC", 253, 13, 32, 8, "bold"); }
    pdf.setDrawColor(78, 85, 85); pdf.setLineWidth(.25); pdf.line(12, 22, 285, 22);
    pdf.setTextColor(245, 247, 246); put(pdf, "AGENDA DE PREVENTIVAS", 12, 32, 140, 12, "bold");
    pdf.setTextColor(184, 194, 190); put(pdf, `${clientName}  /  ${unitName}`, 12, 40, 170, 8);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); pdf.setTextColor(187, 161, 108);
    pdf.text("CALENDÁRIO MENSAL", 285, 30, { align: "right" });
    pdf.setFontSize(13.5); pdf.setTextColor(245, 247, 246);
    pdf.text(monthLabel(month).toUpperCase(), 285, 40, { align: "right" });
    const monthly = sorted.filter((item) => dateKey(item.plannedDate).slice(0, 7) === month);
    const counts = metrics(monthly);
    pdf.setTextColor(48, 68, 101); put(pdf, `${monthly.length} visitas programadas no mês`, 12, 55, 100, 10, "bold");
    const pills = [["Concluídas", counts.done, "done"], ["Hoje / atraso", counts.late, "late"], ["Em andamento", counts.progress, "progress"], ["Aguardando área", counts.waiting, "waiting"], ["No prazo", counts.planned, "planned"]];
    pills.forEach(([label, value, tone], pillIndex) => {
      const x = 124 + pillIndex * 32.2, color = palette[tone];
      pdf.setFillColor(...color.fill); pdf.roundedRect(x, 48, 30.2, 10, 3, 3, "F");
      pdf.setTextColor(...color.text); put(pdf, `${value} ${label}`, x + 2, 54.3, 26.2, 6.4, "bold");
    });
    const first = new Date(`${month}-01T12:00:00`);
    const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const offset = first.getDay();
    const weeks = Math.ceil((offset + count) / 7);
    const top = 69, tableHeight = 125, colWidth = 273 / 7, rowHeight = (tableHeight - 9) / weeks;
    ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"].forEach((day, column) => {
      const x = 12 + column * colWidth;
      pdf.setFillColor(232, 239, 250); pdf.rect(x, top, colWidth, 9, "F");
      pdf.setTextColor(73, 94, 127); put(pdf, day, x + 3, top + 6, colWidth - 6, 7.5, "bold");
    });
    for (let slot = 0; slot < weeks * 7; slot++) {
      const day = slot - offset + 1, column = slot % 7, row = Math.floor(slot / 7);
      const x = 12 + column * colWidth, y = top + 9 + row * rowHeight;
      pdf.setFillColor(day < 1 || day > count ? 246 : 255, day < 1 || day > count ? 248 : 255, day < 1 || day > count ? 252 : 255);
      pdf.rect(x, y, colWidth, rowHeight, "F");
      pdf.setDrawColor(223, 231, 243); pdf.setLineWidth(0.25); pdf.rect(x, y, colWidth, rowHeight);
      if (day < 1 || day > count) continue;
      const date = `${month}-${String(day).padStart(2, "0")}`;
      pdf.setTextColor(37, 53, 80); put(pdf, day, x + 2, y + 4.4, 12, 9, "bold");
      const events = monthly.filter((item) => dateKey(item.plannedDate) === date);
      const available = Math.max(0, Math.floor((rowHeight - 6) / 5.4));
      events.slice(0, available).forEach((item, eventIndex) => {
        const color = palette[statusTone(item.displayStatus)], eventY = y + 6 + eventIndex * 5.4;
        pdf.setFillColor(...color.fill); pdf.roundedRect(x + 1.4, eventY, colWidth - 2.8, 4.8, 1, 1, "F");
        pdf.setFillColor(...color.line); pdf.rect(x + 1.4, eventY, 1, 4.8, "F");
        pdf.setTextColor(...color.text);
        const name = item.groupNumber ? `Grupo ${item.groupNumber}` : item.assetName || item.planName || "Preventiva";
        put(pdf, `${name} · ${item.displayStatus}`, x + 3.3, eventY + 3.3, colWidth - 5, 6.6, "bold");
      });
      if (events.length > available) {
        pdf.setTextColor(79, 94, 117);
        put(pdf, `+${events.length - available} visita(s)`, x + 2, y + rowHeight - 1, colWidth - 4, 6.5);
      }
    }
    pdf.setTextColor(113, 130, 155); put(pdf, "AboutPlan  ·  Agenda de manutenção preventiva", 12, 202, 170, 7.5);
    put(pdf, `${index + 2} / ${months.length + 1}`, 264, 202, 20, 7.5, "bold");
  });
  if (download) pdf.save(`Agenda_preventivas_${unitName || "unidade"}.pdf`);
  return pdf;
}
